/* Guner Office supplemental measurement. Native Google & YouTube owns ecommerce. */
(() => {
  'use strict';
  if (window.GunerMeasurement) return;
  const EVENTS = Object.freeze({
    open: 'personalization_open', start: 'personalization_start', complete: 'personalization_complete',
    color: 'variant_color_select', gallery: 'product_gallery_interaction', video: 'product_video_play',
    reviews: 'reviews_open', shipping: 'shipping_info_open', warranty: 'warranty_open',
    filter: 'collection_filter_used', search: 'site_search', sticky: 'sticky_add_to_cart_click', select: 'select_item'
  });
  const names = new Set(Object.values(EVENTS));
  const configNode = document.querySelector('[data-guner-measurement-config]');
  let config;
  try { config = JSON.parse(configNode?.textContent || '{}'); } catch { return; }
  if (!/^G-[A-Z0-9]+$/.test(config.measurementId || '')) return;
  const seen = new WeakMap();
  const state = node => {
    if (!seen.has(node)) seen.set(node, new Set());
    return seen.get(node);
  };
  const allowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch { return false; }
  };
  // Shopify's consent API is not exposed on every theme until this native feature is requested.
  // Loading it reads the existing visitor choice; it never grants consent.
  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], () => {});
  }
  const parse = node => {
    try { return JSON.parse(node?.textContent || 'null'); } catch { return null; }
  };
  const metadata = scope => parse(scope?.querySelector('[data-guner-measurement-product]'));
  const current = () => metadata(document.querySelector('[data-ga-product-root]')) || config.product;
  const itemFor = (product, variantId, quantity = 1) => {
    if (!product || !/^\d+$/.test(String(product.id))) return null;
    const variant = product.variants?.find(v => String(v.id) === String(variantId));
    if (!variant || !Number.isFinite(variant.price) || variant.price < 0) return null;
    return {
      item_id: `shopify_${config.feedCountry}_${product.id}_${variant.id}`,
      item_name: product.title, item_category: product.type || '', item_brand: product.vendor || '',
      item_variant: variant.title === 'Default Title' ? '' : variant.title,
      price: variant.price / 100, quantity: Number.isFinite(Number(quantity)) ? Math.max(1, Math.floor(Number(quantity) || 1)) : 1
    };
  };
  const productContext = root => {
    const product = metadata(root) || current();
    const variantId = root?.querySelector('[data-ga-variant-id], input[name="id"]')?.value || product?.selectedVariant;
    const quantity = root?.querySelector('[name="quantity"]')?.value || 1;
    const item = itemFor(product, variantId, quantity);
    return item ? {items: [item], value: Math.round(item.price * item.quantity * 100) / 100, currency: config.currency} : {};
  };
  // No form values, search terms, arbitrary URLs, customer fields or line-item properties enter this function.
  const send = (name, context = {}, extra = {}) => {
    if (!names.has(name)) return false;
    if (!allowed()) {
      configNode.dataset.measurementStatus = window.Shopify?.customerPrivacy ? 'consent-denied' : 'waiting-consent-api';
      return false;
    }
    if (typeof window.gtag !== 'function') {
      configNode.dataset.measurementStatus = 'waiting-google-tag';
      return false;
    }
    configNode.dataset.measurementStatus = 'ready';
    const payload = {
      send_to: config.measurementId,
      page_location: location.origin + location.pathname,
      page_title: config.product?.title || config.pageType || 'Storefront',
      page_referrer: '',
      page_type: config.pageType,
      measurement_version: 'guner_v1'
    };
    if (context.items?.length) {
      payload.items = context.items.map(item => ({
        item_id: item.item_id, item_name: item.item_name, item_category: item.item_category,
        item_brand: item.item_brand, item_variant: item.item_variant, price: item.price, quantity: item.quantity
      }));
      payload.currency = context.currency;
      payload.value = context.value;
      payload.product_id = String(context.items[0].item_id).split('_')[2];
      // GA4 serializes items for ecommerce events; custom interactions also need scalar context.
      const primary = context.items[0];
      payload.item_id = primary.item_id;
      payload.item_name = primary.item_name;
      payload.item_category = primary.item_category;
      payload.item_variant = primary.item_variant;
      payload.variant_id = String(primary.item_id).split('_')[3];
      payload.price = primary.price;
      payload.quantity = primary.quantity;
    }
    for (const key of ['interaction_type', 'filter_name', 'item_list_id', 'item_list_name', 'personalization_used']) {
      if (extra[key] !== undefined) payload[key] = extra[key];
    }
    if (config.debug === true) payload.debug_mode = true;
    try { window.gtag('event', name, payload); return true; } catch { return false; }
  };
  const once = (node, key, name, context, extra) => {
    if (state(node).has(key)) return;
    if (send(name, context, extra)) state(node).add(key);
  };
  const rootFor = element => element?.closest('[data-ga-product-root]');
  const personal = element => element?.closest('[data-ga-personalization]');
  document.addEventListener('focusin', event => {
    const panel = personal(event.target);
    if (panel) once(panel, 'open', EVENTS.open, productContext(rootFor(panel)));
  });
  document.addEventListener('input', event => {
    if (!event.target.matches('[data-ga-name], [data-ga-title]') || !event.target.value.trim()) return;
    const panel = personal(event.target);
    if (panel) once(panel, 'start', EVENTS.start, productContext(rootFor(panel)));
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (event.defaultPrevented) return;
    if (form.matches('[data-ga-product-form]')) {
      const panel = form.querySelector('[data-ga-personalization]');
      if (!panel || !panel.querySelector('[data-ga-name]')?.value.trim() || !form.checkValidity()) return;
      const context = productContext(rootFor(form));
      if (context.items) once(panel, 'complete:' + context.items[0].item_id, EVENTS.complete, context, {personalization_used: true});
    } else if (form.matches('form[action*="/search"]') && form.querySelector('[name="q"]')?.value.trim()) {
      send(EVENTS.search, {}, {interaction_type: 'submit'});
    }
  });
  const pendingColors = new WeakSet();
  document.addEventListener('change', event => {
    const input = event.target;
    if (input.matches('[data-ga-color-value], [data-ga-option]')) {
      const root = rootFor(input);
      if (!root || pendingColors.has(root)) return;
      pendingColors.add(root);
      queueMicrotask(() => {
        pendingColors.delete(root);
        const context = productContext(root);
        const key = context.items?.[0]?.item_id;
        if (key && root.dataset.gunerLastMeasuredVariant !== key && send(EVENTS.color, context)) {
          root.dataset.gunerLastMeasuredVariant = key;
        }
      });
    }
    if (input.matches('[name^="filter."]') && config.pageType === 'collection') {
      const filterName = input.name;
      // Filter values are deliberately excluded; merchant-defined filter names only.
      if (/^filter\.[a-zA-Z0-9_.-]{1,80}$/.test(filterName)) send(EVENTS.filter, {}, {
        filter_name: filterName, item_list_id: config.collection?.id, item_list_name: config.collection?.title
      });
    }
  });
  const clickContext = element => productContext(rootFor(element) || document.querySelector('[data-ga-product-root]'));
  document.addEventListener('click', event => {
    const element = event.target.closest?.('a,button,[data-ga-gallery-thumb],[data-guner-event],.jdgm-preview-badge');
    if (!element) return;
    if (element.matches('[data-ga-gallery-thumb], [data-ga-gallery-prev], [data-ga-gallery-next], [data-ga-gallery-link]')) {
      send(EVENTS.gallery, clickContext(element), {interaction_type: element.hasAttribute('data-ga-gallery-thumb') ? 'thumbnail' : element.hasAttribute('data-ga-gallery-link') ? 'zoom' : 'arrow'});
    }
    if (element.matches('.ga-proof__all, a[href="#judgeme_product_reviews"], .jdgm-preview-badge')) send(EVENTS.reviews, clickContext(element));
    if (element.matches('a[href="/policies/shipping-policy"]')) send(EVENTS.shipping, clickContext(element), {interaction_type: 'policy_link'});
    if (element.matches('sticky-add-to-cart button[name="add"], [data-guner-sticky-add]')) send(EVENTS.sticky, clickContext(element));
    // Optional explicit hooks for actual existing open controls; never synthesize absent UI.
    const hook = element.getAttribute('data-guner-event');
    if ([EVENTS.shipping, EVENTS.warranty, EVENTS.reviews, EVENTS.open].includes(hook)) send(hook, clickContext(element));
    if (!element.matches('a[href*="/products/"]')) return;
    const card = element.closest('product-card, .ga-card, [data-guner-product-card]');
    if (!card) return;
    const product = metadata(card);
    if (!product) return;
    const selected = card.querySelector('[data-variant-id]:checked')?.dataset.variantId || product.selectedVariant;
    const item = itemFor(product, selected);
    if (!item) return;
    send(EVENTS.select, {items: [item], currency: config.currency, value: item.price}, {
      item_list_id: config.collection?.id || 'product_recommendations',
      item_list_name: config.collection?.title || 'Product recommendations'
    });
  }, {capture: true});
  document.addEventListener('play', event => {
    if (event.target.tagName === 'VIDEO' && event.target.closest('[data-ga-product-root], media-gallery')) {
      once(event.target, 'play', EVENTS.video, clickContext(event.target), {interaction_type: 'html_video'});
    }
  }, true);
  // Public surface is names/status only. No arbitrary payload or purchase emitter.
  window.GunerMeasurement = Object.freeze({version: '1.0.0', events: EVENTS});
})();
